/**
 * Bridge to the Electron shell, when the app is running inside it.
 *
 * The same React bundle serves the browser and the desktop app, so every
 * desktop-only surface is gated on `isDesktop()`. In a browser `window.brainzr`
 * is undefined and all of this is inert — nothing here may be required for the
 * web client to work.
 *
 * Why the desktop needs its own cast layer at all: the Google Cast Sender SDK
 * is Chrome-only and does not exist in Electron, so the shell speaks castv2
 * directly and exposes it here. See `desktop/src/main/cast.ts`.
 */

export type SurfaceMode = "docked" | "pip";

export interface CastDevice {
  id: string;
  name: string;
  model: string;
  host: string;
  port: number;
  status?: string;
}

export interface MediaLoad {
  url: string;
  contentType: string;
  title: string;
  subtitle?: string;
  image?: string;
  isLive: boolean;
}

export interface PlayRequest {
  url: string;
  userAgent?: string;
  startAt?: number;
  isLive: boolean;
}

interface BrainzrBridge {
  isDesktop: true;
  video: {
    setBounds(b: { x: number; y: number; width: number; height: number }): Promise<void>;
    hide(): Promise<void>;
    /**
     * Picture-in-picture. The SAME surface window mpv is already painting into
     * is detached from the page rectangle and made small, always-on-top and
     * corner-placed — there is no second player and no re-buffer.
     *
     * `state()` exists so a button can render correctly on mount; `onState`
     * alone would leave it guessing until something changed, and PiP can also
     * exit on its own (the window being closed), which would desync it.
     */
    pip?: {
      enter(): Promise<void>;
      leave(): Promise<void>;
      state(): Promise<SurfaceMode>;
      onState(fn: (mode: SurfaceMode) => void): () => void;
    };
  };
  mpv: {
    play(req: PlayRequest): Promise<void>;
    pause(): Promise<void>;
    resume(): Promise<void>;
    seek(seconds: number): Promise<void>;
    setVolume(level: number): Promise<void>;
    setAudioTrack(id: number): Promise<void>;
    setSubtitleTrack(id: number | "no"): Promise<void>;
    stop(): Promise<void>;
    onProperty(fn: (name: string, value: unknown) => void): () => void;
  };
  cast: {
    startDiscovery(): Promise<CastDevice[]>;
    rescan(): Promise<CastDevice[]>;
    list(): Promise<CastDevice[]>;
    connect(device: CastDevice): Promise<CastDevice | null>;
    load(media: MediaLoad): Promise<void>;
    play(): Promise<void>;
    pause(): Promise<void>;
    seek(seconds: number): Promise<void>;
    setVolume(level: number): Promise<void>;
    disconnect(): Promise<void>;
    current(): Promise<CastDevice | null>;
    onDevices(fn: (devices: CastDevice[]) => void): () => void;
  };
}

declare global {
  interface Window {
    brainzr?: BrainzrBridge;
  }
}

export function isDesktop(): boolean {
  return typeof window !== "undefined" && window.brainzr?.isDesktop === true;
}

export function desktopCast(): BrainzrBridge["cast"] | null {
  return isDesktop() ? window.brainzr!.cast : null;
}

/** Native picture-in-picture, when the shell exposes it. Older shells will not,
 *  hence optional — the button is hidden rather than throwing. */
export function desktopPip(): NonNullable<BrainzrBridge["video"]["pip"]> | null {
  return isDesktop() ? (window.brainzr!.video.pip ?? null) : null;
}

/** The bundled mpv player, when running in the desktop shell. Callers must
 *  fall back to the browser engines when this is null. */
export function desktopMpv(): BrainzrBridge["mpv"] | null {
  return isDesktop() ? window.brainzr!.mpv : null;
}

/**
 * Make a playback URL absolute for a Chromecast.
 *
 * The receiver fetches the media itself, over the network, from its own place
 * on the LAN — it has no notion of our origin, so a same-origin path like
 * `/api/playlists/2/play/live/1.ts` is meaningless to it. Getting this wrong
 * produces exactly the symptom that is hardest to attribute: the device
 * connects, reports success, and plays nothing.
 *
 * `origin` is passed in rather than read from `window` so it can be tested.
 */
export function toCastableUrl(url: string, origin: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  try {
    return new URL(url, origin).toString();
  } catch {
    return url;
  }
}
